import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Bot, Globe, Megaphone, Share2, Users, Mail, Facebook, Instagram, Linkedin, TrendingUp, Users2, Award, Target, CheckCircle, Shield, Zap } from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import Button from '../components/ui/Button';
import ConsultationForm from '../components/ConsultationForm';

const AnimatedNumber = ({ value }: { value: string }) => {
  const baseNumber = parseInt(value.replace(/[^0-9]/g, ''));
  const count = useMotionValue(0);
  const rounded = useTransform(count, latest => Math.round(latest));

  useEffect(() => {
    const animation = animate(count, baseNumber, {
      duration: 2,
      ease: "easeOut",
    });

    return animation.stop;
  }, [baseNumber]);

  return (
    <motion.span>{rounded}</motion.span>
  );
};

const testimonials = [
  {
    name: 'Sarah Johnson',
    role: 'CEO, TechStart Inc.',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=150&h=150',
    quote: 'Our digital presence transformed completely. 300% increase in qualified leads within 3 months.',
  },
  {
    name: 'Michael Chen',
    role: 'Marketing Director, GrowthCo',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=150&h=150',
    quote: 'The AI automation solutions saved us 40 hours per week in manual tasks.',
  }
];

const trustedBy = [
  {
    name: 'TechCorp',
    logo: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?auto=format&fit=crop&w=200&h=100',
    industry: 'Technology',
    highlight: 'AI Implementation',
    description: 'Leading provider of enterprise AI solutions with 98% client satisfaction rate',
    stats: {
      projects: '250+',
      satisfaction: '98%',
      growth: '3x'
    },
    icon: Bot,
    color: 'blue',
    bgGradient: 'from-blue-50 to-blue-100/50'
  },
  {
    name: 'HealthPlus',
    logo: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?auto=format&fit=crop&w=200&h=100',
    industry: 'Healthcare',
    highlight: 'Digital Transformation',
    description: 'Revolutionizing healthcare through innovative digital solutions',
    stats: {
      patients: '1M+',
      efficiency: '85%',
      savings: '40%'
    },
    icon: Shield,
    color: 'green',
    bgGradient: 'from-green-50 to-green-100/50'
  },
  {
    name: 'FinanceHub',
    logo: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?auto=format&fit=crop&w=200&h=100',
    industry: 'Finance',
    highlight: 'Process Automation',
    description: 'Automated financial processes saving 10,000+ work hours annually',
    stats: {
      transactions: '5M+',
      automation: '95%',
      accuracy: '99.9%'
    },
    icon: Zap,
    color: 'purple',
    bgGradient: 'from-purple-50 to-purple-100/50'
  },
  {
    name: 'EduTech',
    logo: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?auto=format&fit=crop&w=200&h=100',
    industry: 'Education',
    highlight: 'Platform Development',
    description: 'Empowering 100,000+ students with advanced learning platforms',
    stats: {
      students: '100K+',
      engagement: '89%',
      growth: '4x'
    },
    icon: Globe,
    color: 'orange',
    bgGradient: 'from-orange-50 to-orange-100/50'
  }
];

const stats = [
  { label: 'Clients Worldwide', value: '500+' },
  { label: 'Success Rate', value: '98%' },
  { label: 'Team Experts', value: '50+' }
];

function Home() {
  const [session, setSession] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <header className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1920&q=80')] bg-cover bg-center opacity-10" />
          <div className="absolute inset-0 bg-blue-900/50" />
          
          {/* Animated circles */}
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.2, 0.1],
              }}
              transition={{
                duration: 8,
                delay: i * 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute rounded-full bg-white/10 backdrop-blur-3xl"
              style={{
                width: `${300 + i * 200}px`,
                height: `${300 + i * 200}px`,
                left: `${30 + i * 10}%`,
                top: `${20 + i * 15}%`,
                transform: 'translate(-50%, -50%)'
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-block mb-6"
            >
              <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-lg text-sm font-medium text-white">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse mr-2" />
                Transforming businesses since 2020
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-5xl md:text-7xl font-bold text-white max-w-4xl mx-auto leading-tight"
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 via-blue-100 to-white">
                AI-Driven Digital Growth
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="text-xl md:text-2xl text-blue-100/90 max-w-3xl mx-auto"
            >
              Elevate your business with our comprehensive digital marketing solutions powered by cutting-edge AI technology
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="flex justify-center gap-4 pt-4"
            >
              {session ? (
                <Link to="/dashboard">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="relative group"
                  >
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-blue-400 rounded-lg blur opacity-50 group-hover:opacity-100 transition duration-200" />
                    <Button 
                      size="lg" 
                      className="relative bg-white text-blue-900 hover:bg-blue-50 group px-8"
                    >
                      Go to Dashboard
                    </Button>
                  </motion.div>
                </Link>
              ) : (
                <Link to="/login">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="relative group"
                  >
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-blue-400 rounded-lg blur opacity-50 group-hover:opacity-100 transition duration-200" />
                    <Button 
                      size="lg"
                      className="relative bg-white text-blue-900 hover:bg-blue-50 group px-8"
                    >
                      Get Started
                    </Button>
                  </motion.div>
                </Link>
              )}
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1 }}
              className="grid grid-cols-3 gap-8 mt-16 max-w-3xl mx-auto"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-3xl font-bold text-white mb-2">
                    <AnimatedNumber value={stat.value} />
                    {stat.value.includes('+') ? '+' : '%'}
                  </div>
                  <div className="text-blue-100/80 text-sm">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-50 to-transparent" />
      </header>

      {/* Vision & Mission Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Vision & Mission</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Building the future of digital business solutions
            </p>
          </motion.div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex-1"
            >
              <div className="relative bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg h-full border border-blue-100">
                <div className="absolute -top-6 left-8 w-12 h-12 bg-white rounded-2xl shadow-md flex items-center justify-center">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div className="mt-4">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Vision</h3>
                  <p className="text-gray-600 leading-relaxed">
                    At Soft Standards Inc., we see a future where every business can grow and succeed with smart, easy-to-use digital solutions. We aim to be the trusted partner that helps companies of all sizes boost efficiency, drive innovation, and stay ahead of the competition.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex-1"
            >
              <div className="relative bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg h-full border border-blue-100">
                <div className="absolute -top-6 left-8 w-12 h-12 bg-white rounded-2xl shadow-md flex items-center justify-center">
                  <Award className="w-6 h-6 text-blue-600" />
                </div>
                <div className="mt-4">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Mission</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Our mission is to develop clear and reliable software standards that make everyday business operations smoother and more cost-effective. We listen closely to our customers' needs and create tailored solutions that cut waste, save time, and fuel smart, sustainable growth.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted By Industry Leaders
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Join the ranks of innovative companies transforming their businesses with our solutions
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trustedBy.map((company, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`bg-gradient-to-br ${company.bgGradient} rounded-2xl p-6 shadow-sm 
                  hover:shadow-xl hover:-translate-y-1 transform transition-all duration-300 
                  border border-${company.color}-100/50`}
              >
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <motion.div
                      whileHover={{ rotate: 15 }}
                      className={`w-12 h-12 bg-${company.color}-100 rounded-xl 
                        flex items-center justify-center group-hover:bg-${company.color}-200 
                        transition-colors duration-300`}
                    >
                      <company.icon className={`w-6 h-6 text-${company.color}-600`} />
                    </motion.div>
                    <span className={`text-sm font-medium text-${company.color}-600 
                      bg-${company.color}-100/50 px-3 py-1 rounded-full`}>
                      {company.industry}
                    </span>
                  </div>

                  {/* Content */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{company.name}</h3>
                    <p className="text-gray-600 text-sm mb-4">{company.description}</p>
                    <div className={`text-sm font-medium text-${company.color}-600`}>
                      {company.highlight}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-200/50">
                    {Object.entries(company.stats).map(([key, value], i) => (
                      <div key={i} className="text-center">
                        <div className={`text-lg font-bold text-${company.color}-600`}>
                          {value}
                        </div>
                        <div className="text-xs text-gray-500 capitalize">
                          {key}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Success Indicator */}
                  <div className="flex items-center text-sm text-gray-500">
                    <CheckCircle className={`w-4 h-4 text-${company.color}-500 mr-2`} />
                    Verified Partner
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Consultation Form Section */}
      <section id="consultation" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Book a Free Consultation</h2>
          <ConsultationForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Soft Standards</h3>
              <p className="text-gray-400 mb-4">Transforming businesses through digital excellence</p>
              <div className="flex space-x-4">
                <a 
                  href="https://www.facebook.com/profile.php?id=61562866862991" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a 
                  href="https://www.instagram.com/softstandards" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a 
                  href="https://www.linkedin.com/company/soft-standards-inc" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-gray-400">
                <li>18820B 69th AVE APT 1B New York</li>
                <li>contact@softstandards.net</li>
                <li>(401) 644-1467</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Newsletter</h3>
              <p className="text-gray-400 mb-4">Stay updated with our latest news and updates</p>
              <form className="flex gap-2">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="bg-gray-800 text-white px-4 py-2 rounded-lg flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Button size="sm">
                  <Mail className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 mt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">
                © 2025 Soft Standards. All rights reserved.
              </p>
              <div className="flex gap-4 mt-4 md:mt-0">
                <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;